import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Overline } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  LogOut,
  Flame,
  Users,
  ListMusic,
  CheckCheck,
  ArrowLeft,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "de_admin_token";

const TABS = [
  { id: "received", label: "Incoming" },
  { id: "approved", label: "Approved" },
  { id: "played", label: "Played" },
  { id: "closed", label: "Closed" },
];

const inputCls =
  "bg-white/5 border-white/10 h-12 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-primary/50";

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

export default function Dj() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState(null);
  const [active, setActive] = useState(null);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("received");

  const auth = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadEvents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dj/events`, { headers: auth() });
      setEvents(data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error("Please sign in again.");
      } else {
        toast.error("Could not load events.");
      }
    }
  }, [auth]);

  const loadRequests = useCallback(
    async (eventId) => {
      try {
        const { data } = await axios.get(`${API}/dj/events/${eventId}/requests`, {
          headers: auth(),
        });
        setData(data);
      } catch (e) {}
    },
    [auth]
  );

  useEffect(() => {
    if (token) loadEvents();
  }, [token, loadEvents]);

  useEffect(() => {
    if (!active) return;
    loadRequests(active.id);
    const t = setInterval(() => loadRequests(active.id), 4000);
    return () => clearInterval(t);
  }, [active, loadRequests]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (songKey, status) => {
    try {
      await axios.post(
        `${API}/dj/requests/status`,
        { event_id: active.id, song_key: songKey, status },
        { headers: auth() }
      );
      loadRequests(active.id);
    } catch {
      toast.error("Could not update. Try again.");
    }
  };

  if (!token) {
    return (
      <main data-testid="dj-login-page" className="min-h-screen flex items-center justify-center px-6">
        <form
          data-testid="dj-login-form"
          onSubmit={login}
          className="glass rounded-3xl p-10 sm:p-12 w-full max-w-md space-y-8"
        >
          <div className="text-center">
            <img src="/images/logo.png" alt="Dream Entertainment" className="h-12 w-auto mx-auto mb-6" />
            <Overline>Staff Access</Overline>
            <h1 className="font-serif text-4xl tracking-tight mt-3">
              DJ <em className="text-primary">dashboard.</em>
            </h1>
          </div>
          <div className="space-y-2">
            <Label htmlFor="dj-email">Email</Label>
            <Input
              id="dj-email"
              type="email"
              data-testid="dj-email-input"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@dreamentertainment.org"
              className={inputCls}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="dj-password">Password</Label>
            <Input
              id="dj-password"
              type="password"
              data-testid="dj-password-input"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className={inputCls}
            />
          </div>
          <button
            type="submit"
            data-testid="dj-login-submit"
            disabled={loading}
            className="w-full rounded-full bg-primary text-primary-foreground py-4 text-sm font-medium tracking-widest uppercase transition-[transform,opacity] duration-300 hover:scale-[1.02] disabled:opacity-60 flex items-center justify-center gap-3"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Sign In
          </button>
        </form>
      </main>
    );
  }

  if (!active) {
    return (
      <main data-testid="dj-events-page" className="min-h-screen pt-32 pb-24 px-6 sm:px-12 max-w-4xl mx-auto">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <Overline>DJ Dashboard</Overline>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">
              Your <em className="text-primary">events.</em>
            </h1>
          </div>
          <button
            data-testid="dj-logout-btn"
            onClick={() => {
              localStorage.removeItem(TOKEN_KEY);
              setToken(null);
              setEvents(null);
            }}
            className="rounded-full border border-white/20 px-5 py-2.5 text-sm flex items-center gap-2 hover:border-accent hover:text-accent transition-colors duration-300"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>

        {events === null ? (
          <div className="flex justify-center py-24">
            <Loader2 size={32} className="animate-spin text-primary" />
          </div>
        ) : events.length === 0 ? (
          <p data-testid="dj-no-events" className="text-center text-muted-foreground py-24 font-serif text-2xl">
            No events assigned to you yet.
          </p>
        ) : (
          <div className="space-y-4" data-testid="dj-events-list">
            {events.map((e) => (
              <article
                key={e.id}
                data-testid={`dj-event-card-${e.id}`}
                className="glass rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4"
              >
                <div>
                  <h2 className="font-serif text-2xl">{e.name}</h2>
                  <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground mt-1">
                    {e.event_type} · {e.date}
                    {e.start_time ? ` · ${e.start_time}` : ""}
                  </p>
                  <p className="text-[11px] tracking-widest mt-2">
                    <span className="text-primary">CODE {e.code}</span>
                    <span className={`ml-3 ${e.requests_enabled ? "text-green-400" : "text-muted-foreground"}`}>
                      {e.requests_enabled ? "REQUESTS OPEN" : "REQUESTS CLOSED"}
                    </span>
                  </p>
                </div>
                <button
                  data-testid={`dj-open-event-${e.id}`}
                  onClick={() => setActive(e)}
                  className="rounded-full bg-primary text-primary-foreground px-6 py-3 text-xs font-medium tracking-[0.2em] uppercase transition-transform duration-300 hover:scale-105"
                >
                  Open Live Dashboard
                </button>
              </article>
            ))}
          </div>
        )}
      </main>
    );
  }

  const requests = data?.requests || [];
  const filtered = requests.filter((r) => r.status === tab);
  const counts = Object.fromEntries(
    TABS.map((t) => [t.id, requests.filter((r) => r.status === t.id).length])
  );

  return (
    <main data-testid="dj-live-dashboard" className="min-h-screen pt-32 pb-24 px-6 sm:px-12 max-w-4xl mx-auto">
      <button
        data-testid="dj-back-btn"
        onClick={() => {
          setActive(null);
          setData(null);
          setTab("received");
        }}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors duration-300 mb-6"
      >
        <ArrowLeft size={16} /> All Events
      </button>

      <div className="mb-8">
        <Overline>Live Dashboard</Overline>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">{active.name}</h1>
        <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground mt-2">
          {active.event_type} · {active.date} · Code {active.code}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-8">
        {[
          { label: "Requesters", value: data?.stats.guests ?? 0, Icon: Users, tid: "dj-stat-guests" },
          { label: "Total Requests", value: data?.stats.total ?? 0, Icon: ListMusic, tid: "dj-stat-total" },
          { label: "Played", value: data?.stats.played ?? 0, Icon: CheckCheck, tid: "dj-stat-played" },
        ].map(({ label, value, Icon, tid }) => (
          <div key={label} data-testid={tid} className="glass rounded-2xl p-4 sm:p-6 text-center">
            <Icon size={18} className="text-primary mx-auto" />
            <p className="font-serif text-3xl sm:text-4xl mt-2">{value}</p>
            <p className="text-[9px] sm:text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-1">
              {label}
            </p>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto pb-1" data-testid="dj-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`dj-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`shrink-0 rounded-full px-5 py-2.5 text-xs tracking-[0.2em] uppercase border transition-colors duration-300 ${
              tab === t.id
                ? "bg-primary text-primary-foreground border-primary"
                : "border-white/15 text-muted-foreground hover:border-primary/50 hover:text-primary"
            }`}
          >
            {t.label} ({counts[t.id]})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p data-testid="dj-empty-tab" className="text-center text-muted-foreground py-16 font-serif text-2xl">
          {tab === "received" ? "No incoming requests yet." : `Nothing ${tab} yet.`}
        </p>
      ) : (
        <div className="space-y-3" data-testid="dj-requests-list">
          {filtered.map((r) => (
            <article
              key={r.song_key}
              data-testid={`dj-request-${r.song_key.replace(/[^a-z0-9]+/g, "-")}`}
              className={`glass rounded-2xl p-5 sm:p-6 ${r.count >= 3 ? "border-accent/50" : ""}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="min-w-0">
                  {r.count >= 3 && (
                    <p className="text-[11px] tracking-[0.2em] uppercase text-accent mb-1 flex items-center gap-1.5">
                      <Flame size={13} /> {r.count} Requests
                    </p>
                  )}
                  <h3 className="font-serif text-2xl leading-tight">{r.song_title}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {r.artist} · {r.count} {r.count === 1 ? "request" : "requests"} · last {fmtTime(r.last_at)}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {tab === "received" && active.mode === "approval" && (
                    <button
                      data-testid={`dj-approve-${r.song_key.replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => setStatus(r.song_key, "approved")}
                      className="rounded-full bg-green-500/15 text-green-400 border border-green-500/40 px-4 py-2 text-[11px] tracking-[0.15em] uppercase hover:bg-green-500/25 transition-colors duration-300"
                    >
                      Approve
                    </button>
                  )}
                  {(tab === "received" || tab === "approved") && (
                    <>
                      <button
                        data-testid={`dj-play-${r.song_key.replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => setStatus(r.song_key, "played")}
                        className="rounded-full bg-primary text-primary-foreground px-4 py-2 text-[11px] tracking-[0.15em] uppercase transition-transform duration-300 hover:scale-105"
                      >
                        Mark as Played
                      </button>
                      <button
                        data-testid={`dj-close-${r.song_key.replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => setStatus(r.song_key, "closed")}
                        className="rounded-full border border-white/20 text-muted-foreground px-4 py-2 text-[11px] tracking-[0.15em] uppercase hover:border-accent hover:text-accent transition-colors duration-300"
                      >
                        Close
                      </button>
                    </>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </main>
  );
}
